import Papa from 'papaparse';
import { Transaction, TransactionType } from '../types';

export interface ParsedCsvRow {
  date: Date;
  description: string;
  amount: number;
  direction: 'debit' | 'credit';
}

/** Parses raw CSV text into headers + row objects keyed by header name. */
export function parseCsvText(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const result = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim(),
  });
  return {
    headers: result.meta.fields || [],
    rows: result.data,
  };
}

// Order matters: date and amount/debit/credit hints are specific enough to
// check first; description's hints are checked last and are deliberately
// narrow -- a bare 'transaction' was tried here once and false-matched
// "Transaction Date" before the real description column ("Memo") was ever
// considered, silently mapping the date column to itself as description.
const HEADER_HINTS: Record<string, string[]> = {
  date: ['transaction date', 'posted date', 'posting date', 'date'],
  amount: ['transaction amount', 'amount'],
  debit: ['debit', 'withdrawal', 'withdrawals', 'money out'],
  credit: ['credit', 'deposit', 'deposits', 'money in'],
  description: ['description', 'desc', 'narrative', 'memo', 'payee', 'details'],
};

/**
 * Header-name heuristics -- matches this bank statement's actual column
 * names to our canonical fields. Handles both shapes banks export in:
 * a single signed 'amount' column, or separate 'debit'/'credit' columns.
 * Returns a partial mapping; the caller decides whether what came back is
 * usable or needs the user to confirm/correct it (StatementReviewTable's
 * column-mapping step). Each header can only be claimed by one canonical
 * field -- first match wins, in HEADER_HINTS' own order above.
 */
export function inferColumnMapping(headers: string[]): Record<string, string> {
  const mapping: Record<string, string> = {};
  const lowerHeaders = headers.map(h => ({ raw: h, lower: h.toLowerCase().trim() }));
  const claimed = new Set<string>();

  for (const [canonical, hints] of Object.entries(HEADER_HINTS)) {
    const match = lowerHeaders.find(h => !claimed.has(h.raw) && hints.some(hint => h.lower === hint || h.lower.includes(hint)));
    if (match) {
      mapping[canonical] = match.raw;
      claimed.add(match.raw);
    }
  }

  return mapping;
}

/** True once a mapping has enough to actually parse rows -- a date, a
 *  description, and either a single amount column or a debit/credit pair. */
export function isMappingComplete(mapping: Record<string, string>): boolean {
  const hasAmount = !!mapping.amount || !!mapping.debit || !!mapping.credit;
  return !!mapping.date && !!mapping.description && hasAmount;
}

function parseCsvDate(raw: string, format?: string): Date | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  
  if (format === 'DD/MM/YYYY') {
    const m = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), 12, 0, 0); // year, month, day
  } else if (format === 'MM/DD/YYYY') {
    const m = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (m) return new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]), 12, 0, 0);
  } else if (format === 'YYYY-MM-DD') {
    const m = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  }

  // Fallback to original auto-detection behavior if no format provided or format didn't match
  // yyyy-MM-dd / yyyy/MM/dd
  let m = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  // MM/dd/yyyy or dd/MM/yyyy -- assume US MM/dd/yyyy, the common bank-export default
  m = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m) return new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]), 12, 0, 0);
  const fallback = new Date(trimmed);
  return isNaN(fallback.getTime()) ? null : fallback;
}

function parseAmount(raw: string): number {
  if (!raw) return 0;
  const cleaned = raw.replace(/[$,]/g, '').trim();
  const negative = /^\(.*\)$/.test(cleaned);
  const num = parseFloat(cleaned.replace(/[()]/g, ''));
  if (isNaN(num)) return 0;
  return negative ? -Math.abs(num) : num;
}

/**
 * Maps raw CSV rows to ParsedCsvRow[] using a confirmed column mapping.
 * Single-signed-amount columns: negative = debit, positive = credit
 * (the near-universal bank-export convention). Separate debit/credit
 * columns: whichever one is non-empty/non-zero for a row wins.
 */
export function mapToParsedRows(rawRows: Record<string, string>[], mapping: Record<string, string>): ParsedCsvRow[] {
  const out: ParsedCsvRow[] = [];
  for (const row of rawRows) {
    const date = parseCsvDate(row[mapping.date] || '', mapping.dateFormat);
    if (!date) continue;
    const description = (row[mapping.description] || '').trim();

    let amount: number;
    let direction: 'debit' | 'credit';

    if (mapping.amount) {
      const signed = parseAmount(row[mapping.amount] || '');
      if (signed === 0) continue;
      amount = Math.abs(signed);
      direction = signed < 0 ? 'debit' : 'credit';
    } else {
      const debitVal = mapping.debit ? parseAmount(row[mapping.debit] || '') : 0;
      const creditVal = mapping.credit ? parseAmount(row[mapping.credit] || '') : 0;
      if (Math.abs(debitVal) > 0) {
        amount = Math.abs(debitVal);
        direction = 'debit';
      } else if (Math.abs(creditVal) > 0) {
        amount = Math.abs(creditVal);
        direction = 'credit';
      } else {
        continue;
      }
    }

    out.push({ date, description, amount, direction });
  }
  return out;
}

const fmtDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const normalizeDescription = (desc: string) => desc.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 40);

/** date-to-day + amount-to-cents + normalized description -- stable
 *  enough to catch an exact re-import of the same statement without being
 *  so loose it collides across genuinely different transactions. */
export function buildFingerprint(row: ParsedCsvRow): string {
  return `${fmtDay(row.date)}|${row.amount.toFixed(2)}|${normalizeDescription(row.description)}`;
}

/** True if a Transaction is linked to the given account (either side --
 *  for a bank account, as its own bankAccountId or as a transfer's
 *  toBankAccountId; for a debt, as its creditCardId or as the linkedId of
 *  a debt_payment). */
function transactionMatchesAccount(tx: Transaction, accountId: string, accountType: 'bank_account' | 'debt'): boolean {
  if (accountType === 'bank_account') {
    return tx.bankAccountId === accountId || tx.toBankAccountId === accountId;
  }
  return tx.creditCardId === accountId || (tx.type === 'debt_payment' && tx.linkedId === accountId);
}

const MS_PER_DAY = 86400000;

/**
 * Classifies one parsed row against the account's existing transactions.
 * Same amount to the cent, date within 1 day -> duplicate (pre-excluded by
 * the caller, still re-includable). Within 3 days -> possible_duplicate
 * (flagged, not pre-excluded). Otherwise -> new.
 */
function transactionMatchesDirection(tx: Transaction, rowDirection: 'debit' | 'credit', accountId: string, accountType: 'bank_account' | 'debt'): boolean {
  if (accountType === 'bank_account') {
    if (rowDirection === 'credit') {
      return tx.type === 'income' || (tx.type === 'transfer' && tx.toBankAccountId === accountId);
    } else {
      return tx.type === 'expense' || tx.type === 'spend' || tx.type === 'savings_contribution' || tx.type === 'debt_payment' || (tx.type === 'transfer' && tx.bankAccountId === accountId);
    }
  } else {
    if (rowDirection === 'debit') {
      return tx.type === 'expense' || tx.type === 'spend';
    } else {
      return tx.type === 'debt_payment';
    }
  }
}

export function classifyRowStatus(
  row: ParsedCsvRow,
  existingTransactions: Transaction[],
  accountId: string,
  accountType: 'bank_account' | 'debt'
): { status: 'new' | 'duplicate' | 'possible_duplicate'; duplicateOfTxIds?: string[] } {
  let bestDayDiff = Infinity;
  const duplicateIds: string[] = [];

  for (const tx of existingTransactions) {
    if (!transactionMatchesAccount(tx, accountId, accountType)) continue;
    if (!transactionMatchesDirection(tx, row.direction, accountId, accountType)) continue;
    if (Math.abs(tx.amount - row.amount) > 0.001) continue;
    const dayDiff = Math.abs(tx.date.getTime() - row.date.getTime()) / MS_PER_DAY;
    
    if (dayDiff <= 3) {
      if (dayDiff < bestDayDiff) bestDayDiff = dayDiff;
      if (tx.id) duplicateIds.push(tx.id);
    }
  }

  if (bestDayDiff <= 1) return { status: 'duplicate', duplicateOfTxIds: duplicateIds };
  if (bestDayDiff <= 3) return { status: 'possible_duplicate', duplicateOfTxIds: duplicateIds };
  return { status: 'new' };
}

/**
 * The default transaction type + category for a parsed row, purely for
 * display in the review table -- `commitStatementImport`
 * (functions/src/statementReconciliation.ts) independently derives the
 * same mapping server-side rather than trusting a client-provided type,
 * since that's what actually decides which collection/field gets
 * incremented. Keep these two in sync if the mapping ever changes:
 *
 *   bank account + credit (money in)  -> income,       balance += amount
 *   bank account + debit  (money out) -> expense,       balance -= amount
 *   debt (card/LOC) + debit (a charge)-> expense,        currentAmount += amount
 *   debt (card/LOC) + credit (payment)-> debt_payment,   currentAmount -= amount
 */
/** The generic, non-descriptive category labels defaultRowClassification
 *  hands out before any real categorization happens. Excluded whenever
 *  building the "existing categories" list fed to suggestCategories --
 *  otherwise Gemini sees "Imported Expense" as a legitimate category
 *  already in use and happily suggests it right back, defeating the
 *  whole point. */
export const GENERIC_IMPORT_CATEGORIES = new Set([
  'Imported Deposit',
  'Imported Expense',
  'Imported Charge',
  'Imported Payment',
]);

export function defaultRowClassification(
  direction: 'debit' | 'credit',
  accountType: 'bank_account' | 'debt'
): { type: TransactionType; category: string } {
  if (accountType === 'bank_account') {
    return direction === 'credit'
      ? { type: 'income', category: 'Imported Deposit' }
      : { type: 'expense', category: 'Imported Expense' };
  }
  return direction === 'debit'
    ? { type: 'expense', category: 'Imported Charge' }
    : { type: 'debt_payment', category: 'Imported Payment' };
}
